import React, { useState, useMemo, useEffect } from 'react';
import { type Rechnung, type RechnungsStatus } from '../types';
import { generateRechnungPDF, rechnungDateiname } from '../services/pdfGenerator';
import { downloadEbInterface } from '../services/ebInterface';
import { downloadSteuerberaterCSV } from '../services/steuerberaterExport';
import { downloadBmdNtcsExport } from '../../../services/bmdExport';
import { formatEuroDisplay, formatDateDE } from '../services/rechnungBuilder';
import {
  loadRechnungen, updateRechnungsStatus, loadAusstellerForRechnung,
} from '../../../services/rechnungenService';
import { logAktion } from '../../../services/auditLogService';
import type { Aussteller } from '../types';

// ── Status-Badge ──────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<RechnungsStatus, { label: string; color: string }> = {
  entwurf:   { label: 'Entwurf',   color: 'bg-gray-100 text-gray-700' },
  gestellt:  { label: 'Offen',     color: 'bg-yellow-100 text-yellow-800' },
  bezahlt:   { label: 'Bezahlt',   color: 'bg-green-100 text-green-800' },
  gemahnt:   { label: 'Gemahnt',   color: 'bg-red-100 text-red-700' },
  storniert: { label: 'Storniert', color: 'bg-gray-100 text-gray-500 line-through' },
};

// ── Hauptkomponente ────────────────────────────────────────────────────────

export function RechnungsArchiv(): React.JSX.Element {
  const [rechnungen, setRechnungen] = useState<Rechnung[]>([]);
  const [aussteller,  setAussteller]  = useState<Aussteller | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [filterStatus, setFilterStatus] = useState<RechnungsStatus | 'alle'>('alle');
  const [search, setSearch]             = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    void Promise.all([loadRechnungen(), loadAusstellerForRechnung()]).then(([rech, aus]) => {
      setRechnungen(rech);
      setAussteller(aus);
      setLoading(false);
    });
  }, []);

  const reload = (): void => { void loadRechnungen().then(setRechnungen); };

  const filtered = useMemo(() => rechnungen.filter(r => {
    const matchStatus = filterStatus === 'alle' || r.status === filterStatus;
    const q = search.toLowerCase();
    const matchSearch = q === '' || r.rechnungsNr.toLowerCase().includes(q)
                                 || r.empfaenger.firma.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  }), [rechnungen, filterStatus, search]);

  const year = new Date().getFullYear();
  const monthStart = `${year}-${String(new Date().getMonth() + 1).padStart(2,'0')}-01`;
  const summeOffen   = rechnungen.filter(r => r.status === 'gestellt' || r.status === 'gemahnt')
                                  .reduce((s, r) => s + r.summeBrutto, 0);
  const summeBezahlt = rechnungen.filter(r => r.status === 'bezahlt' && r.datum >= monthStart)
                                  .reduce((s, r) => s + r.summeBrutto, 0);

  const handleStatusChange = (nr: string, status: RechnungsStatus): void => {
    const r = rechnungen.find(x => x.rechnungsNr === nr);
    void updateRechnungsStatus(nr, status).then(() => {
      void logAktion(r?.empfaenger.firma ?? nr, 'RECHNUNG_STATUS', { nr, status });
      reload();
    });
  };

  const handleDownloadPdf = (r: Rechnung): void => {
    if (aussteller === null) { return; }
    const blob = generateRechnungPDF(r, aussteller);
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = rechnungDateiname(r); a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadXml = (r: Rechnung): void => {
    if (aussteller === null) { return; }
    downloadEbInterface(r, aussteller);
  };

  const zeitraum = new Date().toLocaleDateString('de-AT', { month: 'long', year: 'numeric' });

  const handleExportSteuerberater = (): void => {
    downloadSteuerberaterCSV(filtered, zeitraum);
    setShowExportMenu(false);
  };

  const handleExportBmd = (): void => {
    downloadBmdNtcsExport(filtered, zeitraum);
    setShowExportMenu(false);
  };

  const handleBulkMahnen = (): void => {
    const faellig = rechnungen.filter(r => r.status === 'gestellt');
    if (faellig.length === 0) { return; }
    if (!window.confirm(`${faellig.length} offene Rechnung(en) als „Gemahnt" markieren?`)) { return; }
    void Promise.all(
      faellig.map(r => updateRechnungsStatus(r.rechnungsNr, 'gemahnt'))
    ).then(reload);
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-400">Lade Rechnungen…</div>;
  }

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Rechnungsarchiv</h1>
          <p className="text-sm text-gray-500 mt-0.5">Alle Rechnungen + Mahnwesen</p>
        </div>
        <div className="flex gap-2">
          {rechnungen.some(r => r.status === 'gestellt') && (
            <button onClick={handleBulkMahnen} className="btn-secondary text-sm text-orange-600 border-orange-200 hover:bg-orange-50">
              ⚠ Alle mahnen
            </button>
          )}
          <div className="relative">
            <button onClick={() => { setShowExportMenu(v => !v); }} className="btn-secondary text-sm">
              ↓ Export ▾
            </button>
            {showExportMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => { setShowExportMenu(false); }} />
                <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-1 min-w-56">
                  <button onClick={handleExportSteuerberater}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-start gap-3">
                    <span className="text-base mt-0.5">📊</span>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">Steuerberater-CSV</p>
                      <p className="text-xs text-gray-500">Standard-Format mit USt-Aufschlüsselung</p>
                    </div>
                  </button>
                  <button onClick={handleExportBmd}
                    className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-start gap-3">
                    <span className="text-base mt-0.5">🏢</span>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">BMD NTCS</p>
                      <p className="text-xs text-gray-500">Österreich-Standard, Direktimport in BMD</p>
                    </div>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* KPI-Karten */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card">
          <p className="text-xs text-gray-500">Offen / Gemahnt</p>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
            {(summeOffen / 100).toLocaleString('de-AT', { minimumFractionDigits: 2 })} €
          </p>
        </div>
        <div className="card">
          <p className="text-xs text-gray-500">Bezahlt (lfd. Monat)</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
            {(summeBezahlt / 100).toLocaleString('de-AT', { minimumFractionDigits: 2 })} €
          </p>
        </div>
        <div className="card">
          <p className="text-xs text-gray-500">Rechnungen gesamt</p>
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-400 mt-1">{rechnungen.length}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input type="search" value={search} onChange={e => { setSearch(e.target.value); }}
            placeholder="Rechnungs-Nr. oder Firma…"
            className="input-field pl-9 text-sm" />
        </div>
        {(['alle', 'gestellt', 'gemahnt', 'bezahlt', 'storniert'] as const).map(s => (
          <button key={s}
            onClick={() => { setFilterStatus(s); }}
            className={`text-xs px-3 py-1 rounded-full font-medium transition-colors
              ${filterStatus === s
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
              }`}
          >
            {s === 'alle' ? 'Alle' : STATUS_CONFIG[s].label}
          </button>
        ))}
      </div>

      {/* Tabelle */}
      <div className="card overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Nr.</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Datum</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Empfänger</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 hidden sm:table-cell">Status</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Betrag</th>
              <th className="px-2 w-24" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-gray-400 italic">
                {rechnungen.length === 0
                  ? 'Noch keine Rechnungen — im Tab „Neue Rechnung" erstellen und speichern.'
                  : 'Keine Rechnungen gefunden.'
                }
              </td></tr>
            )}
            {filtered.map(r => {
              const sc = STATUS_CONFIG[r.status];
              const isGemahnt = r.status === 'gemahnt';
              return (
                <tr key={r.rechnungsNr}
                  className={`transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50
                    ${isGemahnt ? 'bg-red-50/40 dark:bg-red-950/10' : ''}`}>
                  <td className="px-4 py-3 font-mono text-xs font-bold text-gray-700 dark:text-gray-300">
                    {r.rechnungsNr}
                    {isGemahnt && <span className="ml-1.5 text-red-500">⚠</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{formatDateDE(r.datum)}</td>
                  <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                    {r.empfaenger.firma}
                    <span className="ml-2 text-xs text-gray-400">{r.mandantId}</span>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <select
                      value={r.status}
                      onChange={e => { handleStatusChange(r.rechnungsNr, e.target.value as RechnungsStatus); }}
                      className={`text-xs px-2 py-0.5 rounded-full font-medium border-0 cursor-pointer ${sc.color}`}>
                      {(Object.keys(STATUS_CONFIG) as RechnungsStatus[]).map(s => (
                        <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-medium text-gray-900 dark:text-gray-100">
                    {formatEuroDisplay(r.summeBrutto)}
                  </td>
                  <td className="px-2 py-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => { handleDownloadPdf(r); }}
                        className="p-1.5 text-xs text-gray-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-lg transition-colors"
                        title="PDF">📄</button>
                      <button onClick={() => { handleDownloadXml(r); }}
                        className="p-1.5 text-xs text-gray-500 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950/30 rounded-lg transition-colors"
                        title="eRechnung XML">🗂</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 text-center">
        ✅ Persistenz in Dexie v5 · Status direkt in der Tabelle änderbar
      </p>
    </div>
  );
}
